import { RowActions } from '@/features/approvals/components/RowActions';
import { useEventConfig } from '@/shared/config/event-config-context';
import { cn } from '@/shared/lib/cn';
import { formatRegisteredAtParts } from '@/shared/lib/datetime';
import type { Participant } from '@/shared/types/pax';

interface ParticipantsTableProps {
  participants: Participant[];
  currentGroup: string;
  pendingIds: ReadonlySet<number>;
  onMove: (participant: Participant, target: string) => void;
}

/** Valor ausente: um travessão discreto comunica melhor que uma célula vazia. */
function Value({ children }: { children: string | null }) {
  return children ? <>{children}</> : <span className="text-ink-subtle">—</span>;
}

/**
 * Tabela de participantes (desktop).
 *
 * `<table>` de verdade — não grid de divs: leitores de tela anunciam
 * linha/coluna, e o cabeçalho `sticky` mantém o contexto em listas de 100 itens.
 * No mobile, `ApprovalsPage` renderiza `ParticipantCards` no lugar desta.
 *
 * As colunas do meio são as `columns` da parametrização: rótulo e ordem vêm de lá,
 * e o valor é buscado pelo id do atributo em `participant.values`.
 */
export function ParticipantsTable({ participants, currentGroup, pendingIds, onMove }: ParticipantsTableProps) {
  const { columns } = useEventConfig();

  return (
    <div className="max-h-[calc(100dvh-20rem)] overflow-auto">
      <table className="w-full border-collapse text-left text-sm">
        <caption className="sr-only">Participantes do grupo selecionado</caption>

        <thead className="sticky top-0 z-10">
          <tr className="bg-surface-muted/95 backdrop-blur-sm">
            {/* Só Cadastro e Ações são estruturais; todo o resto vem da
                parametrização, inclusive nome e e-mail se o wizard os marcou. */}
            {[...columns.map(column => column.label), 'Cadastro'].map(heading => (
              <th
                key={heading}
                scope="col"
                className="border-b border-line px-4 py-3 text-[0.75rem] font-semibold tracking-wide text-ink-muted uppercase"
              >
                {heading}
              </th>
            ))}
            <th scope="col" className="border-b border-line px-4 py-3 text-right text-[0.75rem] font-semibold tracking-wide text-ink-muted uppercase">
              Ações
            </th>
          </tr>
        </thead>

        <tbody className="divide-y divide-line">
          {participants.map(participant => {
            const pending = pendingIds.has(participant.id);
            const registered = formatRegisteredAtParts(participant.registeredAt);

            return (
              <tr key={participant.id} className={cn('transition-colors duration-150 hover:bg-brand-50/40', pending && 'opacity-55')}>
                {columns.map(column => (
                  <td key={column.attributeId} className="px-4 py-3 text-ink-muted">
                    <Value>{participant.values[column.attributeId] ?? null}</Value>
                  </td>
                ))}
                <td className="tabular px-4 py-3 whitespace-nowrap text-ink-muted">
                  {registered.date}
                  {registered.time ? <span className="ml-1.5 text-ink-subtle">{registered.time}</span> : null}
                </td>
                <td className="px-4 py-3">
                  <RowActions participant={participant} currentGroup={currentGroup} pending={pending} onMove={onMove} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
